/* =============================================================================
 * rosterNeed — the single source for the concrete roster-need headline (plan 3.1).
 * -----------------------------------------------------------------------------
 * ONE formatter consumed by SchoolCard and SchoolProfileModal (and anywhere else)
 * so the card and the modal can never speak two different languages for the same
 * data. Names the grad-year ONLY when it's the athlete's actual class (a clamped
 * athlete beyond the data horizon stays position-only — bug #123).
 * ========================================================================== */

export interface RosterNeedInput {
  athlete_position?: string | null;
  athlete_graduation_year?: number | null;
  roster_status?: string | null;
  openings?: number | null;
  position_openings?: Array<{
    position_name?: string | null;
    count?: number | null;
    estimated_graduation_year?: number | null;
  }> | null;
}

export interface RosterNeed {
  headline: string;
  sub: string | null;
  // green (open / a real match), amber (limited), red (no openings), neutral (unknown).
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  // The moat: a position + class specific opening ("Needs a 2028 pitcher"). This
  // is the highest-value signal we surface, so the band renders it as the hero.
  strong?: boolean;
}

export function rosterNeed(school: RosterNeedInput): RosterNeed | null {
  // Precise position-level need when per-position openings are present.
  const pos = (school.athlete_position || '').trim();
  const openings = school.position_openings;
  if (pos && Array.isArray(openings) && openings.length) {
    const matched = openings.filter(
      (o) => (o.position_name || '').trim().toLowerCase() === pos.toLowerCase() && (o.count || 0) > 0
    );
    const total = matched.reduce((sum, o) => sum + (o.count || 0), 0);
    if (total > 0) {
      const posLabel = pos.toLowerCase();
      const openingYear = matched[0].estimated_graduation_year;
      const trueYear = school.athlete_graduation_year;
      const showYear = trueYear != null && openingYear != null && Number(trueYear) === Number(openingYear);
      return {
        headline: showYear ? `Needs a ${openingYear} ${posLabel}` : `Needs a ${posLabel}`,
        sub: `${total} ${total === 1 ? 'spot' : 'spots'} open for your class`,
        tone: 'success',
        strong: true,
      };
    }
  }

  // Fallback: roster_status-based concrete need on a green/amber/red status scale
  // (open = green, limited = amber, none = red).
  const status = school.roster_status || undefined;
  const spots = school.openings && school.openings > 0 ? school.openings : 0;
  if (status === 'Open Roster') {
    return { headline: 'Recruiting your class', sub: spots ? `${spots} ${spots === 1 ? 'spot' : 'spots'} open` : 'Roster spots available', tone: 'success' };
  }
  if (status === 'Limited Spots') {
    return { headline: 'Limited spots for your class', sub: spots ? `${spots} open` : null, tone: 'warning' };
  }
  if (status === 'No Openings') {
    return { headline: 'No openings for your class', sub: null, tone: 'danger' };
  }
  return null;
}
